import React, { useState } from "react";
import { z } from "zod";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import type { Freeze } from "../../../shared/types/models/freeze";

interface InstrumentDetailsCardProps {
  property: Freeze;
  setProperty: (p: Freeze) => void;
  onContinue: () => void;
  stepConfig?: {
    fields?: Array<{ key: string; label: string; type?: string; required?: boolean; description?: string }>;
    [key: string]: any;
  };
}

const InstrumentDetailsCard: React.FC<InstrumentDetailsCardProps> = ({ property, setProperty, onContinue, stepConfig }) => {
  const { county, apiBaseUrl } = useUserContext();
  const { getToken } = useKeycloak();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialDataList = property?.countyIndexes && property.countyIndexes.length > 0 
    ? property.countyIndexes.map((ci: any) => ({ id: ci.id, data: ci.recordingIndexes || {} })) 
    : [{ data: {} }];
  const [indexesDataList, setIndexesDataList] = useState<{ id?: number, data: Record<string, string> }[]>(initialDataList);

  React.useEffect(() => {
    if (property?.countyIndexes && property.countyIndexes.length > 0) {
      setIndexesDataList(property.countyIndexes.map((ci: any) => ({ id: ci.id, data: ci.recordingIndexes || {} })));
    }
  }, [property?.countyIndexes]);
  const [errorsList, setErrorsList] = useState<Record<string, string>[]>([{}]);
  const [touchedList, setTouchedList] = useState<Record<string, boolean>[]>([{}]);

  const handleAddIndex = () => {
    setIndexesDataList([...indexesDataList, { data: {} }]);
    setErrorsList([...errorsList, {}]);
    setTouchedList([...touchedList, {}]);
  };

  const handleRemoveIndex = (indexToRemove: number) => {
    setIndexesDataList(indexesDataList.filter((_, i) => i !== indexToRemove));
    setErrorsList(errorsList.filter((_, i) => i !== indexToRemove));
    setTouchedList(touchedList.filter((_, i) => i !== indexToRemove));
  };

  const parsedSchema = typeof county?.documentSchema === 'string'
    ? (() => { try { return JSON.parse(county.documentSchema); } catch { return null; } })()
    : county?.documentSchema;

  // Prefer step-level config fields over top-level documentSchema indexes
  const indexes = stepConfig?.fields || parsedSchema?.indexes || [];

  const getDynamicSchema = () => {
    let schemaObj: Record<string, any> = {};
    indexes.forEach((idx: any) => {
      const isReq = idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0;
      if (isReq) {
        schemaObj[idx.key] = z.string().min(1, `${idx.label} is required`);
      } else {
        schemaObj[idx.key] = z.string().optional();
      }
    });
    return z.object(schemaObj);
  };

  const schema = getDynamicSchema();
  const isFormValid = indexesDataList.every(item => schema.safeParse(item.data).success);

  const handleBlur = (listIdx: number, key: string, value: string) => {
    setTouchedList(tList => {
      const newList = [...tList];
      newList[listIdx] = { ...(newList[listIdx] || {}), [key]: true };
      return newList;
    });
    const fieldSchema = getDynamicSchema().shape[key];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrorsList(eList => {
        const newList = [...eList];
        const currentErrors = { ...(newList[listIdx] || {}) };
        if (!result.success) {
          currentErrors[key] = result.error.errors[0].message;
        } else {
          delete currentErrors[key];
        }
        newList[listIdx] = currentErrors;
        return newList;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Touch all
    const newTouchedList = indexesDataList.map(() => {
      const newTouched: Record<string, boolean> = {};
      indexes.forEach((idx: any) => {
        newTouched[idx.key] = true;
      });
      return newTouched;
    });
    setTouchedList(newTouchedList);

    let hasErrors = false;
    const newErrorsList = indexesDataList.map(item => {
      const result = schema.safeParse(item.data);
      if (!result.success) {
        hasErrors = true;
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach(err => {
          if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
        });
        return fieldErrors;
      }
      return {};
    });

    if (hasErrors) {
      setErrorsList(newErrorsList);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property?.lookup}/freeze-indexes`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ indexes: indexesDataList.map(item => ({ id: item.id, recordingIndexes: item.data })) }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to save instrument details");
      }
      
      const updatedFreezeRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property?.lookup}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const updatedFreeze = await updatedFreezeRes.json();
      
      if (updatedFreeze?.data) {
        setProperty(updatedFreeze.data);
      }
      
      setSaving(false);
      onContinue();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSaveError(err.message || "Failed to save instrument details");
      } else {
        setSaveError("Failed to save instrument details");
      }
      setSaving(false);
    }
  };

  if (!indexes || indexes.length === 0) {
    return (
      <div className="w-full text-center text-gray-500 py-4">
        No instrument details required for this county.
        <button
          onClick={onContinue}
          className="w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-4 hover:bg-[#23235a] transition-colors"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} autoComplete="off">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
        <p className="text-sm text-blue-700">
          Enter the top-level Instrument or Freeze details. If you are protecting a portfolio of multiple properties and do not have a single reference document, you may enter "N/A" for required fields.
        </p>
      </div>

      {indexesDataList.map((item, listIdx) => (
        <div key={listIdx} className="bg-white border border-[#D7DFEC] rounded-lg p-4 relative flex flex-col gap-4">
          {indexesDataList.length > 1 && (
            <button
              type="button"
              onClick={() => handleRemoveIndex(listIdx)}
              className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
              aria-label="Remove"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          {indexes.map((idx: any) => (
            <div key={idx.key} className="flex flex-col gap-1 w-full">
              <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative">
                {idx.label} {idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0 && <span className="text-red-500">*</span>}
                {idx.description && (
                  <span className="relative group cursor-pointer ml-1">
                    <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
                      {idx.description}
                    </span>
                  </span>
                )}
              </label>
              <input
                type="text"
                autoComplete="new-password"
                value={item.data[idx.key] || ''}
                onChange={e => {
                  const newList = [...indexesDataList];
                  newList[listIdx] = { ...newList[listIdx], data: { ...newList[listIdx].data, [idx.key]: e.target.value } };
                  setIndexesDataList(newList);
                }}
                onBlur={e => handleBlur(listIdx, idx.key, e.target.value)}
                className={`w-full px-3 py-2 rounded border ${(errorsList[listIdx]?.[idx.key] && touchedList[listIdx]?.[idx.key]) ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-semibold font-plus-jakarta focus:outline-none focus:ring-1 focus:ring-[#0B4BB6]`}
                placeholder={idx.label}
              />
              {errorsList[listIdx]?.[idx.key] && touchedList[listIdx]?.[idx.key] && <span className="text-red-500 text-xs mt-0.5">{errorsList[listIdx][idx.key]}</span>}
            </div>
          ))}
        </div>
      ))}

      <button
        type="button"
        onClick={handleAddIndex}
        className="w-full border-2 border-dashed border-[#0B4BB6] text-[#0B4BB6] font-bold text-[15px] font-plus-jakarta py-2.5 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
      >
        <span>+ Add County Index</span>
      </button>

      <button
        type="submit"
        className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-4 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] ${(!isFormValid || saving) ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={!isFormValid || saving}
      >
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
      {saveError && <div className="text-red-500 mt-2">{saveError}</div>}
    </form>
  );
};

export default InstrumentDetailsCard;
